<template>
  <SoDialog
    v-model="value"
    width="600"
    :card-title="modalTitle"
    @on-close="onClose"
  >
    <template v-if="!successfulSubmission">
      <p>
        <SoAlert type="warning" class="mb-5">
          Check the customer is the account holder and is authorised to set up
          direct debits on this bank account.
        </SoAlert>
      </p>
      <SoForm
        v-model="isFormValid"
        :autocomplete="false"
        data-testid="dd-form"
        @submit="onSubmit"
      >
        <SoTextField
          v-model="form.accountHoldersName"
          label="Account holder's name"
          placeholder="John Doe"
          :rules="[rules.required]"
          type="text"
          data-testid="dd-form-account-holder"
        />
        <SoTextField
          v-model="form.accountNumber"
          placeholder="12345678"
          label="Account number"
          type="number"
          :rules="[rules.required, rules.accountNumber]"
          data-testid="dd-form-account-number"
        />
        <SoTextField
          v-model="form.sortCode"
          placeholder="123456"
          label="Sort code"
          :rules="[rules.required, rules.sortCode]"
          type="number"
          data-testid="dd-form-sort-code"
        />

        <SoCheckbox
          v-model="form.isCustomerAuthorised"
          label="Is the customer authorised to set up Direct Debit?"
          class="mt-5"
          :rules="[rules.isTruthy, rules.required]"
          data-testid="dd-form-authorised"
        />

        <SoAlert v-if="form.isCustomerAuthorised" type="warning" class="mt-5">
          So Energy will need to pass personal data on to our Direct Debit
          processor, GoCardless in order to process monthly payments from you.
          Your bank statements will show Direct Debits from So Energy or
          GoCardless. The
          <a :href="externalRoutes.DDHelpPage.path" target="_blank"
            >Direct Debit Guarantee</a
          >
          is available in your documents and your online account. Let me know if
          you’d like me to read it.
        </SoAlert>

        <SoCheckbox
          v-if="form.isCustomerAuthorised"
          v-model="form.guaranteeConfirmed"
          :rules="[rules.isTruthy, rules.required]"
          data-testid="dd-form-guarantee"
          label="Has the Direct Debit Guarantee been explained?"
          class="mt-1"
        />

        <template #actions="{ validate }">
          <SoAlert
            v-if="formSubmissionError"
            data-testid="dd-form-error"
            class="mt-5"
            type="error"
            ><p>There was an error when processing the direct debit.</p>
            <p>{{ errorDetails }}</p>
          </SoAlert>
          <div class="d-flex mt-8">
            <SoButton
              data-testid="dd-form-cancel-btn"
              title="Cancel"
              type="secondary"
              large
              @click="onClose"
            />
            <SoButton
              data-testid="dd-form-submit-btn"
              data-dd-action-name="directDebitSubmitButton"
              :loading="isSubmitting"
              :disabled="!isFormValid"
              title="Submit Direct Debit Details"
              type="primary"
              class="ml-auto"
              large
              submit
              @click="validate()"
            />
          </div>
        </template>
      </SoForm>
    </template>
    <template v-if="successfulSubmission && !rejectPaymentSchedulePrompt">
      <p data-testid="dd-form-schedule-msg">
        Do you want to set up a payment schedule now?
      </p>
      <div class="d-flex justify-end mt-8">
        <SoButton
          data-testid="dd-form-no-schedule-btn"
          type="secondary"
          title="No"
          class="mr-2"
          large
          @click="rejectPaymentSchedulePrompt = true"
        />
        <SoButton
          type="primary"
          title="Yes"
          data-dd-action-name="directDebitScheduleButton"
          large
          :to="{ name: RouteNames.CustomerReviewPaymentAmount }"
        />
      </div>
    </template>
    <template v-if="successfulSubmission && rejectPaymentSchedulePrompt">
      <p data-testid="dd-form-cancel-warning-msg">
        This will put the customer onto variable Direct Debit - their payment
        will be their total bill amount including any debt on the account! Are
        you sure?
      </p>
      <div class="d-flex justify-end mt-8">
        <SoButton
          data-testid="dd-form-confirm-no-btn"
          type="secondary"
          title="No"
          class="mr-2"
          large
          @click="rejectPaymentSchedulePrompt = false"
        />
        <SoButton
          data-testid="dd-form-confirm-cancel-btn"
          data-dd-action-name="directDebitDeclineScheduleButton"
          type="primary"
          title="Yes"
          large
          @click="onClose"
        />
      </div>
    </template>
  </SoDialog>
</template>
<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import rules from "@/validators/rules"
  import { RouteNames } from "@/types/router"
  import SoAlert from "@/components/SoAlert.vue"
  import { DirectDebitService } from "@/services/DirectDebitService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    CreateDirectDebitResponse,
    PaymentMethodVerificationResponse,
  } from "@/api/backofficeRest/generated/model"
  import TrackingService from "@/services/TrackingService"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { externalRoutes } from "@/constants/routes/externalRoutes"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoForm,
      SoCheckbox,
      SoTextField,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue", "close"],
    data() {
      return {
        current: useCurrentAccountStore(),
        successfulSubmission: false,
        rejectPaymentSchedulePrompt: false,
        formSubmissionError: false,
        errorDetails: "",
        RouteNames,
        isFormValid: false,
        form: {
          accountHoldersName: "",
          accountNumber: "",
          sortCode: "",
          isCustomerAuthorised: false,
          guaranteeConfirmed: false,
        },
        verifyPaymentMethod: DirectDebitService.verifyPaymentMethod(),
        submitDirectDebitRequest: DirectDebitService.setUpDirectDebit(),
        paymentVerified: false,
        externalRoutes,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      rules() {
        return rules
      },
      isSubmitting() {
        return (
          this.verifyPaymentMethod.isPending ||
          this.submitDirectDebitRequest.isPending
        )
      },
      modalTitle() {
        let title = "Set Up Direct Debit"
        if (this.successfulSubmission && !this.rejectPaymentSchedulePrompt) {
          title = "Direct Debit details submitted"
        } else if (
          this.successfulSubmission &&
          this.rejectPaymentSchedulePrompt
        ) {
          title = "Are you sure?"
        }
        return title
      },
    },
    methods: {
      onClose() {
        this.$emit("close")
        this.resetForm()
      },
      async onSubmit() {
        if (!this.isFormValid) return

        try {
          await this.submitDirectDebit()
        } catch (error) {
          this.formSubmissionError = true
          this.errorDetails = errorHandler.userMessage(error, "")
        }
      },
      async submitDirectDebit() {
        const verifyResponse = (await this.verifyPaymentMethod.mutateAsync({
          bankAccountNumber: this.form.accountNumber,
          sortCode: this.form.sortCode,
        })) as PaymentMethodVerificationResponse

        if (verifyResponse?.success) {
          this.paymentVerified = true
        } else {
          this.formSubmissionError = true
          this.errorDetails = verifyResponse?.error || ""
        }

        if (!this.paymentVerified) return

        const billingAccountId = this.current.coreId.data
        if (!billingAccountId) throw new Error("Missing coreId")

        const submissionResponse =
          (await this.submitDirectDebitRequest.mutateAsync({
            accountId: billingAccountId,
            bankAccountName: this.form.accountHoldersName,
            bankAccountNumber: this.form.accountNumber,
            bankSortCode: this.form.sortCode,
          })) as CreateDirectDebitResponse

        if (submissionResponse?.success) {
          this.successfulSubmission = true
          TrackingService.sendEvent("SetUpDirectDebit:submit:success")
        } else {
          this.formSubmissionError = true
        }
      },
      resetForm() {
        this.formSubmissionError = false
        this.errorDetails = ""
        this.successfulSubmission = false
        this.paymentVerified = false
        this.rejectPaymentSchedulePrompt = false
        this.form.accountHoldersName = ""
        this.form.accountNumber = ""
        this.form.sortCode = ""
        this.form.isCustomerAuthorised = false
        this.form.guaranteeConfirmed = false
      },
    },
  })
</script>
